/**
 * Pet Care Essential supplement recommendation (probiotics only).
 *
 * Rule (requested):
 * 1) Focus only on probiotics species.
 * 2) Species below normal (Deficient / Slightly deficient) are "lacking".
 * 3) Pick supplement with highest units for lacking species first.
 * 4) Then pick next supplement(s) to cover remaining lacking species.
 */

import {
  BIOPET_ESSENTIAL_SPECIES_GROUPS,
  evaluateBiopetEssentialRow,
  getBiopetAgeStage,
  buildQuartileBySpeciesFromGrids
} from './calculate_biopet_essential';

export const BIOPET_ESSENTIAL_SUPPLEMENT_FORMULA = {
  PetGard: {
    'Lactobacillus acidophilus': 100,
    'Lactobacillus rhamnosus': 100
  },
  PetReno: {
    'Lactobacillus acidophilus': 500,
    'Lactobacillus plantarum': 100,
    'Streptococcus thermophilus': 500
  },
  PetFlow: {
    'Lactobacillus plantarum': 500,
    'Lactobacillus rhamnosus': 100,
    'Streptococcus thermophilus': 100
  }
};

/**
 * Image slot presets by recommended supplement count.
 * Edit positions here to control page-8 supplement image placement.
 * - Coordinates are relative to the page-8 overlay wrapper.
 */
export const BIOPET_ESSENTIAL_SUPPLEMENT_SLOT_BY_COUNT = {
  1: [{ x: 380, y: -140, width: 240, height: 240 }],
  2: [
    { x: 230, y: -140, width: 240, height: 240 },
    { x: 530, y: -140, width: 240, height: 240 }
  ],
  3: [
    { x: 80, y: -140, width: 240, height: 240 },
    { x: 380, y: -140, width: 240, height: 240 },
    { x: 680, y: -140, width: 240, height: 240 }
  ]
};

const LACKING_LABEL_WEIGHT = {
  Deficient: 2,
  'Slightly deficient': 1
};

/**
 * Which product to recommend when nothing is lacking.
 *
 * In the browser this reads the user's own setting from localStorage. The server-side
 * PDF render has no localStorage, so the download request forwards the user's current
 * mode/stock and they arrive here as `opts` — keeping the generated PDF identical to
 * the preview the user was looking at. Falls back to 'PetFlow' when neither is available.
 */
function resolveNoLackingBiopetSupplement(opts = {}) {
  const readStored = (key) => {
    try {
      return typeof localStorage === 'undefined' ? null : localStorage.getItem(key);
    } catch {
      return null;
    }
  };

  try {
    const mode = opts.noLackingMode || readStored('biopet_no_lacking_mode') || 'petflow';
    if (mode === 'petgard') return 'PetGard';
    if (mode === 'petreno') return 'PetReno';
    if (mode === 'petflow') return 'PetFlow';
    if (mode === 'auto-lowest') {
      let parsed = opts.stockSnapshot;
      if (!parsed) {
        const rawStock = readStored('biopet_stock_snapshot');
        parsed = rawStock ? JSON.parse(rawStock) : {};
      }
      const ranked = [
        { name: 'PetGard', stock: Number(parsed?.PetGard ?? 0) },
        { name: 'PetReno', stock: Number(parsed?.PetReno ?? 0) },
        { name: 'PetFlow', stock: Number(parsed?.PetFlow ?? 0) }
      ].sort((a, b) => a.stock - b.stock);
      return ranked[0]?.name || 'PetFlow';
    }
  } catch {
    // ignore storage/parse issues and use fallback
  }
  return 'PetFlow';
}

/**
 * Resolve slots for current recommendation count.
 * Priority:
 * 1) `customByCount` (if provided)
 * 2) default `BIOPET_ESSENTIAL_SUPPLEMENT_SLOT_BY_COUNT`
 * 3) `fallbackSlots`
 */
export function resolveBiopetSupplementSlots(
  recommendedSupplements,
  fallbackSlots = [],
  customByCount = null
) {
  const count = Math.min(Math.max((recommendedSupplements || []).length, 0), 3);
  if (count <= 0) return [];
  const byCount = customByCount || BIOPET_ESSENTIAL_SUPPLEMENT_SLOT_BY_COUNT;
  const exact = byCount?.[count];
  if (Array.isArray(exact) && exact.length > 0) {
    return exact.slice(0, count);
  }
  return (fallbackSlots || []).slice(0, count);
}

function toLackingSpeciesMap(probioticRows) {
  const lacking = {};
  (probioticRows || []).forEach((row) => {
    const name = row?.speciesName;
    const label = row?.label;
    const weight = LACKING_LABEL_WEIGHT[label] || 0;
    if (!name || !weight) return;
    lacking[name] = Math.max(lacking[name] || 0, weight);
  });
  return lacking;
}

function scoreSupplementForUncoveredSpecies(supplementName, uncoveredSpecies, lackingWeightBySpecies) {
  const recipe = BIOPET_ESSENTIAL_SUPPLEMENT_FORMULA[supplementName] || {};
  return Object.entries(recipe).reduce((total, [species, units]) => {
    if (!uncoveredSpecies.has(species)) return total;
    const weight = lackingWeightBySpecies[species] || 0;
    return total + (units * weight);
  }, 0);
}

/**
 * Pet Care Professional / CSV row → supplement names using the same probiotic + quartile rules as PDF page 4.
 *
 * @param {Record<string, unknown>} formData - Row data (species columns, optional `age`, `universal` / `Universal bacteria`)
 * @param {Record<string, string[][]>|null} quartileGridBySpecies - per-species 3×3 (Q1–Q3 × Young, Adult, Senior); thresholds use Q2/Q3 rows
 * @returns {string[]}
 */
export function getPetCareProRecommendedSupplementList(formData, quartileGridBySpecies = null) {
  if (!formData || typeof formData !== 'object') return [];
  const ageStage = getBiopetAgeStage(formData.age ?? formData.Age);
  const quartileBySpecies = buildQuartileBySpeciesFromGrids(quartileGridBySpecies);
  const universal = formData.universal ?? formData['Universal bacteria'] ?? 0;

  const probioticRows = BIOPET_ESSENTIAL_SPECIES_GROUPS.probiotics.map((speciesName) =>
    evaluateBiopetEssentialRow({
      speciesName,
      group: 'probiotics',
      speciesValue: formData[speciesName],
      universalValue: universal,
      ageStage,
      quartileBySpecies,
      fallbackQ2: 0,
      fallbackQ3: 0
    })
  );

  const { recommendedSupplements } = recommendBiopetEssentialSupplements(probioticRows);
  return Array.isArray(recommendedSupplements) ? recommendedSupplements : [];
}

export function recommendBiopetEssentialSupplements(probioticRows, opts = {}) {
  const lackingWeightBySpecies = toLackingSpeciesMap(probioticRows);
  const lackingSpecies = Object.keys(lackingWeightBySpecies);
  if (lackingSpecies.length === 0) {
    return {
      lackingSpecies: [],
      // Business rule: configurable fallback when no probiotics are lacking.
      recommendedSupplements: [resolveNoLackingBiopetSupplement(opts)],
      coveredSpecies: [],
      uncoveredSpecies: []
    };
  }

  const remaining = new Set(lackingSpecies);
  const candidates = Object.keys(BIOPET_ESSENTIAL_SUPPLEMENT_FORMULA);
  const selected = [];

  while (remaining.size > 0) {
    let best = null;
    let bestScore = 0;
    candidates.forEach((name) => {
      if (selected.includes(name)) return;
      const score = scoreSupplementForUncoveredSpecies(name, remaining, lackingWeightBySpecies);
      if (score > bestScore) {
        best = name;
        bestScore = score;
      }
    });

    if (!best || bestScore <= 0) break;
    selected.push(best);
    const recipe = BIOPET_ESSENTIAL_SUPPLEMENT_FORMULA[best] || {};
    Object.keys(recipe).forEach((species) => {
      if (remaining.has(species)) remaining.delete(species);
    });
  }

  const coveredSet = new Set();
  selected.forEach((supplementName) => {
    const recipe = BIOPET_ESSENTIAL_SUPPLEMENT_FORMULA[supplementName] || {};
    Object.keys(recipe).forEach((species) => {
      if (lackingWeightBySpecies[species]) coveredSet.add(species);
    });
  });

  const coveredSpecies = Array.from(coveredSet);
  const uncoveredSpecies = lackingSpecies.filter((species) => !coveredSet.has(species));

  // Lacking species with no matching strain in BIOPET_ESSENTIAL_SUPPLEMENT_FORMULA (e.g. B. infantis,
  // E. faecium) yield score 0 for every product — avoid empty recommendations and a dead CSV preview.
  if (selected.length === 0 && lackingSpecies.length > 0) {
    selected.push(resolveNoLackingBiopetSupplement(opts));
  }

  return {
    lackingSpecies,
    recommendedSupplements: selected,
    coveredSpecies,
    uncoveredSpecies
  };
}
